import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, ChevronLeft, FileText, HelpCircle, Sparkles } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { ToolIcon, brandColorFor } from "../classic/panels";
import { toolGlyphIdFor, toolNameInText } from "../classic/toolIcons";
import { Shimmer } from "../StatCard";
import { HourStrip, RollupBars } from "./charts";
import { SERVER_URL } from "../../../../utils/serverConfig";
import { paper } from "../../screens/graphical/viz";

/**
 * The work log — what the person actually worked on.
 *
 * Three surfaces, deliberately separated so each answers one question:
 *
 *   TakeawayList     Level 1. ONE LINE per stretch: what, how long, which
 *                    project. No prose, no breakdown. Scannable in seconds.
 *   StretchAnalysis  Level 2, focused. Everything about ONE stretch — opened by
 *                    clicking its takeaway, with prev/next so the reader can
 *                    walk the day without going back out.
 *   FullWorkLog      Level 2, whole window. Every day, every stretch, expanded.
 *
 * The unclear case is a first-class state, not an error: a stretch with no
 * written analysis renders as "couldn't be named" and says why. Inventing a
 * title from app names would be the single easiest way to make this feature
 * untrustworthy.
 */

/* ═══════════════════════════════════════════════════════════════════════════
   Prose → something readable.

   The writer emits one dense paragraph per stretch, and it is genuinely
   information-rich — file names, paths, URLs, ticket counts. Rendered as a
   single block it is a wall, and the specifics (the most valuable part) are the
   first thing the eye slides off.

   This splits it into beats and marks up the artefacts. It changes NO words: it
   is a typographic transform, so a reader can still recognise the sentence the
   model actually wrote.

   Sentence splitting on `. ` alone shreds this content — "activity_monitor.log
   and…", "team.css.precrm.bak", "teamgrid.ai/dashboard/…", "localhost:3000/…"
   and "C:\Users\…\Local." all contain full stops. So artefacts are masked out
   FIRST, the masked text is split, and the artefacts are restored as chips.
   ═══════════════════════════════════════════════════════════════════════════ */

const ARTEFACT_PATTERNS = [
  /https?:\/\/\S+/gi,                                             // absolute URLs
  /\blocalhost:\d+[^\s,;)]*/gi,                                   // localhost:3000/…
  /\b[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)+\.(?:ai|com|net|io|org|dev|co|app|in)\b(?:\/[^\s,;)]*)?/gi,
  /\b[a-z0-9-]+\.(?:ai|com|net|io|org|dev|co|app|in)\/[^\s,;)]*/gi, // teamgrid.ai/dashboard/…
  /\b[A-Za-z]:\\[^\s,;)]+/g,                                      // C:\Users\…
  /(?:^|\s)(\/[A-Za-z0-9._-]+){2,}\/?/g,                          // /unix/style/paths
  /\b[\w.-]+\.(?:pdf|md|log|zip|css|scss|js|jsx|ts|tsx|json|bak|png|jpe?g|svg|csv|xlsx?|docx?|html?|sh|ps1|py|txt|ya?ml|env|sql|conf|ini)\b/gi,
  /"[^"\n]{2,80}"/g,                                              // quoted strings
  /'[^'\n]{2,80}'/g,
];

const SENT_SPLIT = /(?<=[.!?])\s+(?=[A-Z(“"'])/;

export function segmentProse(raw) {
  const text = String(raw || "").replace(/\s+/g, " ").trim();
  if (!text) return [];

  // 1. mask
  const store = [];
  let masked = text;
  for (const re of ARTEFACT_PATTERNS) {
    masked = masked.replace(re, (hit) => {
      const lead = hit.startsWith(" ") ? " " : "";
      let body = hit.trim();
      if (!body) return hit;
      /* Peel sentence punctuation off the tail and hand it back to the prose.
         Without this, `C:\\Users\\...\\AppData\\Local.` eats its own full stop, the
         splitter never sees a boundary, and two beats fuse into one. */
      let tail = "";
      while (body.length > 1 && /[.,;:!?)]$/.test(body)) {
        // A closing paren is only punctuation when it is unbalanced.
        if (body.endsWith(")") && (body.match(/\(/g) || []).length >= (body.match(/\)/g) || []).length) break;
        tail = body.slice(-1) + tail;
        body = body.slice(0, -1);
      }
      if (!body) return hit;
      store.push(body);
      return `${lead}\u0001${store.length - 1}\u0001${tail}`;
    });
  }

  // 2. split, then 3. restore into token runs the renderer can style
  return masked
    .split(SENT_SPLIT)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((sentence) =>
      sentence
        // \u0001 is the private placeholder sentinel the masker above wrote; it
        // cannot occur in prose, so the control-character rule does not apply.
        // eslint-disable-next-line no-control-regex
        .split(/\u0001(\d+)\u0001/)
        .map((part, i) => (i % 2 === 1
          ? { kind: "artefact", value: store[Number(part)] }
          : { kind: "text", value: part }))
        .filter((tok) => tok.value)
    );
}

/**
 * @param {string}  text
 * @param {boolean} compact  one paragraph instead of beats — used where the
 *                           surface is narrow enough that markers cost more
 *                           than they give.
 * @param {boolean} numbered graphical drill variant: 01/02 sequence numbers
 *                           instead of dot markers (mock-approved). Classic
 *                           call sites never pass it, so classic is unchanged.
 */
export function Prose({ text, compact = false, numbered = false, activities = null }) {
  const t = useDashTheme();
  const beats = React.useMemo(() => segmentProse(text), [text]);
  if (!beats.length) return null;

  const chip = (v, k) => (
    <code key={k} title={v} style={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: "0.88em", padding: "1px 5px", borderRadius: t.r.xs,
      background: t.c.surfaceMuted, color: t.c.text,
      boxShadow: `inset 0 0 0 1px ${t.c.border}`,
      wordBreak: "break-all", lineHeight: 1.45,
    }}>{v}</code>
  );

  const render = (beat, bi) => beat.map((tok, i) => (
    tok.kind === "artefact" ? chip(tok.value, `${bi}-${i}`) : <React.Fragment key={`${bi}-${i}`}>{tok.value}</React.Fragment>
  ));

  if (compact || beats.length === 1) {
    return (
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.7, color: t.c.textSecondary }}>
        {beats.map(render)}
      </p>
    );
  }

  if (numbered) {
    const ppr = paper(t);
    return (
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
        {beats.map((beat, bi) => {
          const raw = beat.map((tok) => tok.value).join("");
          const tool = toolHintFromPoint(raw, activities) || (activities && activities[0] ? { name: activities[0].label } : null);
          return (
          <li key={bi} style={{
            display: "grid", gridTemplateColumns: "32px 1fr", gap: 12, alignItems: "start",
            padding: "11px 12px", borderRadius: 12,
            background: t.isDark ? ppr.well : "#fff",
            boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
          }}>
            {tool ? <ToolIcon name={tool.name} type={tool.type} size={32} /> : (
              <span style={{
                width: 32, height: 32, borderRadius: 9, display: "grid", placeItems: "center",
                background: t.c.primary, color: "#fff",
                fontSize: 12, fontWeight: 750, fontVariantNumeric: "tabular-nums",
              }}>{String(bi + 1).padStart(2, "0")}</span>
            )}
            <span style={{ fontSize: 14, fontWeight: 550, lineHeight: 1.4, letterSpacing: "-0.15px", color: t.c.text, overflowWrap: "break-word", minWidth: 0 }}>
              {render(beat, bi)}
            </span>
          </li>
          );
        })}
      </ol>
    );
  }

  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 9 }}>
      {beats.map((beat, bi) => (
        <li key={bi} style={{ display: "grid", gridTemplateColumns: "12px 1fr", gap: 9, alignItems: "start" }}>
          <span aria-hidden style={{
            width: 5, height: 5, borderRadius: 99, marginTop: 8,
            background: t.c.primary, opacity: 0.55, justifySelf: "center",
          }} />
          <span style={{ fontSize: 13.5, lineHeight: 1.65, color: t.c.textSecondary }}>
            {render(beat, bi)}
          </span>
        </li>
      ))}
    </ul>
  );
}

const SecLabel = ({ t, children, hint }) => (
  <div style={{
    display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
    ...t.font.microLabel,
    color: t.c.textTertiary, marginBottom: 10,
  }}>
    <span>{children}</span>
    {hint && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, fontSize: 11 }}>{hint}</span>}
  </div>
);

function Pill({ t, tone = "muted", children, title }) {
  const map = {
    // no grey chips (2026-08-30): every chip carries a real tone
    muted: { bg: `${t.status.info}1c`, fg: t.status.info },
    dur: { bg: t.c.primarySoft, fg: t.c.primary },
    project: { bg: t.c.aiSoft, fg: t.c.ai },
    warn: { bg: t.status.warningSoft, fg: t.status.warning },
    ok: { bg: t.status.positiveSoft || `${t.status.positive}1c`, fg: t.status.positive },
  };
  const s = map[tone] || map.muted;
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 5, height: 24, padding: "0 10px",
      borderRadius: 999, fontSize: 11.5, fontWeight: 650, border: `1px solid ${s.fg}44`,
      background: s.bg, color: s.fg, whiteSpace: "nowrap",
      fontVariantNumeric: tone === "dur" ? "tabular-nums" : "normal",
      maxWidth: 190, overflow: "hidden", textOverflow: "ellipsis",
    }}>{children}</span>
  );
}

/** Intensity → the node colour on the spine. An unclear stretch is never green. */
function nodeColor(t, s) {
  if (s.unclear) return t.status.warning;
  if (s.intensity === "high") return t.status.positive;
  if (s.intensity === "low") return t.c.textTertiary;
  return t.c.primary;
}

/* ═══════════════════════════════════════════════════════════════════════════
   LEVEL 1 — the takeaways
   ═══════════════════════════════════════════════════════════════════════════ */

export function DayHeadline({ t, worklog }) {
  if (!worklog?.headline) return null;
  const single = worklog.range?.days === 1;
  return (
    <div style={{
      padding: `13px 15px`, borderRadius: t.r.md, marginBottom: 12,
      background: `linear-gradient(180deg, ${t.c.primarySoft}, ${t.c.surface})`,
      boxShadow: `0 0 0 1px ${t.c.border}`,
    }}>
      <div style={{
        ...t.font.microLabel,
        color: t.c.primary, marginBottom: 6,
      }}>
        {single ? "The day in one line" : "The window in one line"}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5, fontWeight: 500, letterSpacing: "-0.1px", color: t.c.text }}>
        {worklog.headline}
      </div>
      <div style={{
        marginTop: 9, fontSize: 11.5, color: t.c.textTertiary, display: "flex",
        gap: 8, flexWrap: "wrap", fontVariantNumeric: "tabular-nums",
      }}>
        <span>{worklog.coverage?.stretches || 0} stretch{worklog.coverage?.stretches === 1 ? "" : "es"}</span>
        <span>·</span>
        <span>{worklog.totalFormatted} accounted</span>
        {worklog.range?.days > 1 && (
          <>
            <span>·</span>
            <span>{worklog.coverage?.daysWithNarrative} of {worklog.range.days} days</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The compact list. One row per stretch, grouped by day when the window spans
 * more than one. `index` is stable across the whole window so a row and its
 * analysis are always the same object.
 */
export function TakeawayList({ worklog, focusIndex, onPick }) {
  const t = useDashTheme();
  const days = worklog?.days || [];
  const multi = (worklog?.range?.days || 1) > 1;

  let running = -1;
  const rows = [];

  for (const day of days) {
    if (!day.stretches.length) continue;
    if (multi) {
      rows.push(
        <div key={`h-${day.dateLocal}`} style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between",
          gap: 10, margin: rows.length ? "16px 0 8px" : "0 0 8px",
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: t.c.text }}>{day.label}</span>
          <span style={{ fontSize: 11, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
            {day.totalFormatted}
          </span>
        </div>
      );
      if (day.headline) {
        rows.push(
          <div key={`hl-${day.dateLocal}`} style={{
            fontSize: 12.5, color: t.c.textSecondary, lineHeight: 1.55, marginBottom: 10,
          }}>{day.headline}</div>
        );
      }
    }

    for (const s of day.stretches) {
      running += 1;
      const idx = running;
      const on = idx === focusIndex;
      rows.push(
        <button
          key={s.id || idx}
          onClick={() => onPick(idx)}
          title={s.summary || "No written analysis for this stretch"}
          style={{
            display: "grid", gridTemplateColumns: "44px 12px 1fr", gap: 8,
            width: "100%", border: "none", background: "none", padding: 0,
            textAlign: "left", cursor: "pointer", fontFamily: t.font.family, color: t.c.text,
          }}
        >
          <span style={{ paddingTop: 12 }}>
            <span style={{
              display: "block", fontSize: 11.5, fontWeight: 600, lineHeight: 1,
              color: t.c.textSecondary, fontVariantNumeric: "tabular-nums",
            }}>{s.t0 || "—"}</span>
            {s.t1 && (
              <span style={{
                display: "block", fontSize: 10.5, marginTop: 3, color: t.c.textTertiary,
                fontVariantNumeric: "tabular-nums",
              }}>{s.t1}</span>
            )}
          </span>

          <span style={{ position: "relative" }}>
            <span style={{
              position: "absolute", left: 5, top: 22, bottom: -8, width: 2,
              borderRadius: 2, background: t.c.border,
            }} />
            <span style={{
              position: "absolute", left: 1, top: 12, width: 10, height: 10, borderRadius: 99,
              background: t.c.surface, boxShadow: `0 0 0 2.5px ${nodeColor(t, s)}`,
            }} />
          </span>

          <span style={{
            display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 10,
            padding: "10px 11px 10px 13px", borderRadius: t.r.md, background: t.c.surface,
            boxShadow: on ? `0 0 0 2px ${t.c.primary}` : `0 0 0 1px ${t.c.border}`,
            marginBottom: 8,
          }}>
            <span style={{ minWidth: 0 }}>
              <span style={{
                display: "block", fontSize: 13.5, fontWeight: 600, lineHeight: 1.35,
                letterSpacing: "-0.1px",
                color: s.unclear ? t.c.textSecondary : t.c.text,
              }}>{s.title}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                <Pill t={t} tone="dur">{s.durationLabel}</Pill>
                {s.project
                  ? <Pill t={t} tone="project" title={s.project}>{s.project}</Pill>
                  : <Pill t={t} tone="warn">no project</Pill>}
              </span>
            </span>
            <ChevronRight size={15} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
          </span>
        </button>
      );
    }
  }

  if (!rows.length) return null;
  return <div>{rows}</div>;
}

/** Nothing written for this window — said plainly, with the reason. */
/**
 * OverviewCard — the solid, real-content read of a person, at the top of the
 * drill-down. Not a generic density summary: it leads with the actual work
 * (window/page titles), the active/idle split, when they worked, and where the
 * hours pooled — every figure from tracked data.
 */
export function OverviewCard({ t, name, position, metrics, activity, spark }) {
  const m = metrics || {};
  const tracked = m.trackedMinutes || 0;
  const active = m.activeMinutes || 0;
  const idle = m.idleMinutes || 0;
  const activePct = tracked > 0 ? Math.round((active / tracked) * 100) : 0;
  const items = (activity && activity.items) || [];
  const apps = (activity && activity.apps) || [];
  const fmtM = (x) => (x >= 60 ? `${Math.floor(x / 60)}h ${x % 60}m` : `${x}m`);
  const peakLabel = m.peakHour && (m.peakHour.label || m.peakHour.range || null);
  const ini = String(name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const appHue = (n) => (t.accentFor ? t.accentFor(String(n || "app")).fg : t.c.primary);

  const chips = [
    peakLabel ? { k: "Peak", v: peakLabel } : null,
    apps.length ? { k: "Apps & sites", v: activity.distinct || apps.length } : null,
    items.length ? { k: "Work items", v: items.length } : null,
  ].filter(Boolean);

  return (
    <div style={{ padding: "16px 17px", borderRadius: t.r.md, background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}, 0 6px 18px -14px ${t.c.shadowColor}` }}>
      {/* identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14 }}>
        <span style={{ width: 38, height: 38, borderRadius: 999, display: "grid", placeItems: "center", fontSize: 14, fontWeight: 600, flexShrink: 0, background: t.c.primarySoft || t.c.surfaceMuted, color: t.c.primary }}>{ini}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: 600, color: t.c.text, letterSpacing: "-0.2px" }}>{name}</div>
          {position ? <div style={{ fontSize: 12, color: t.c.textTertiary, marginTop: 1 }}>{position}</div> : null}
        </div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.8px", color: t.c.text, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{fmtM(tracked)}</div>
          <div style={{ fontSize: 10.5, color: t.c.textTertiary, marginTop: 3 }}>tracked</div>
        </div>
      </div>

      {/* active / idle split */}
      {tracked > 0 && (
        <div style={{ marginBottom: chips.length ? 13 : 0 }}>
          <div style={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden", gap: 2 }}>
            <span style={{ width: `${activePct}%`, background: t.status.positive, borderRadius: 999 }} />
            <span style={{ width: `${100 - activePct}%`, borderRadius: 999, backgroundImage: `repeating-linear-gradient(-45deg, ${t.status.warning} 0 3px, ${t.c.surfaceMuted} 3px 7px)` }} />
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 7, fontSize: 11, color: t.c.textTertiary }}>
            <span><b style={{ color: t.c.text }}>{fmtM(active)}</b> active · {activePct}%</span>
            <span><b style={{ color: t.c.text }}>{fmtM(idle)}</b> idle</span>
          </div>
        </div>
      )}

      {/* meta chips */}
      {chips.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: (items.length || spark) ? 14 : 0 }}>
          {chips.map((c) => (
            <span key={c.k} style={{ fontSize: 11, color: t.c.textSecondary, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`, borderRadius: 8, padding: "5px 10px" }}>
              {c.k} <b style={{ color: t.c.text }}>{c.v}</b>
            </span>
          ))}
        </div>
      )}

      {/* in focus — the actual work, top 3 */}
      {items.length > 0 && (
        <div style={{ marginBottom: spark ? 14 : 0 }}>
          <div style={{ ...t.font.microLabel, color: t.c.textTertiary, marginBottom: 9 }}>In focus</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {items.slice(0, 3).map((it, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                {it.app ? <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", color: appHue(it.app), background: `${appHue(it.app)}1a`, padding: "2px 6px", borderRadius: 5, flexShrink: 0, whiteSpace: "nowrap" }}>{it.app}</span> : null}
                <span style={{ fontSize: 12.5, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }} title={it.title}>{it.title}</span>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtM(it.minutes)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* when — the day spark passed in by the caller */}
      {spark ? (
        <div>
          <div style={{ ...t.font.microLabel, color: t.c.textTertiary, marginBottom: 7 }}>When</div>
          {spark}
        </div>
      ) : null}
    </div>
  );
}

export function ActivityBreakdown({ t, activity }) {
  if (!activity?.apps?.length && !activity?.items?.length) return null;
  const fmtM = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
  const items = activity.items || [];
  const apps = activity.apps || [];
  const topItem = items.length ? Math.max(...items.map((i) => i.minutes), 1) : 1;
  const topApp = apps.length ? Math.max(...apps.map((a) => a.minutes), 1) : 1;

  // app badge colour — one hue per app so the eye can group the list
  const appHue = (name) => (t.accentFor ? t.accentFor(String(name || "app")).fg : t.c.primary);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* ── the actual work: real window & page titles, by time ─────────── */}
      {items.length > 0 && (
        <div style={{ padding: "13px 14px", borderRadius: t.r.md, background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}` }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: t.c.text }}>What was open, in detail</span>
            <span style={{ fontSize: 11, color: t.c.textTertiary }}>{items.length} items · by active time</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((it, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 54px", alignItems: "center", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                    {it.app && (
                      <span style={{
                        ...t.font.microLabel,
                        color: appHue(it.app), background: `${appHue(it.app)}1a`,
                        padding: "2px 6px", borderRadius: 5, flexShrink: 0, whiteSpace: "nowrap",
                      }}>{it.app}</span>
                    )}
                    <span style={{ fontSize: 12.5, color: t.c.text, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.title}>
                      {it.title}
                    </span>
                  </div>
                  <span style={{ display: "block", height: 5, background: t.c.surfaceMuted, borderRadius: 999, overflow: "hidden" }}>
                    <span style={{ display: "block", height: "100%", borderRadius: 999, width: `${Math.max(4, (it.minutes / topItem) * 100)}%`, background: appHue(it.app) }} />
                  </span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, textAlign: "right", color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{fmtM(it.minutes)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── the apps & sites summary — where the hours pooled ───────────── */}
      {apps.length > 0 && (
        <div style={{ padding: "13px 14px", borderRadius: t.r.md, background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}` }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 11 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: t.c.text }}>Where the hours pooled</span>
            <span style={{ fontSize: 11, color: t.c.textTertiary }}>{activity.distinct} apps &amp; sites · {fmtM(activity.totalMinutes)}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {apps.slice(0, 8).map((a) => (
              <div key={a.name} style={{ display: "grid", gridTemplateColumns: "132px 1fr 54px", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: t.c.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.name}>
                  {a.name}
                  <span style={{ color: t.c.textTertiary, marginLeft: 6, fontSize: 10.5 }}>{a.type === "website" ? "site" : "app"}</span>
                </span>
                <span style={{ height: 12, background: t.c.surfaceMuted, borderRadius: 4, overflow: "hidden" }}>
                  <span style={{ display: "block", height: "100%", borderRadius: 4, width: `${Math.max(3, (a.minutes / topApp) * 100)}%`, background: a.type === "website" ? t.c.ai : t.c.primary }} />
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, textAlign: "right", color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{fmtM(a.minutes)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 10.5, color: t.c.textTertiary, lineHeight: 1.5 }}>
        Real window and page titles by active time, PII removed. No written narrative has
        been generated for this window yet — this is the account of what filled the hours.
      </div>
    </div>
  );
}

export function WorkLogEmpty({ t, worklog, name }) {
  const who = (name || "This person").split(" ")[0];
  const activity = worklog?.activity;
  const hasActivity = activity?.apps?.length > 0;

  // When the agent recorded app/site activity, THAT is the answer to "what did
  // they work on" — the written sentence + the breakdown. No redundant "no
  // analysis" banner on top; the breakdown's own footnote states the caveat.
  if (hasActivity) {
    return <ActivityBreakdown t={t} activity={activity} />;
  }

  // Genuinely nothing — no narrative AND no activity.
  return (
    <div style={{
      display: "flex", gap: 10, padding: "13px 14px", borderRadius: t.r.md,
      background: t.c.surfaceMuted, boxShadow: `0 0 0 1px ${t.c.border}`,
    }}>
      <HelpCircle size={16} style={{ color: t.c.textTertiary, flexShrink: 0, marginTop: 1 }} />
      <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t.c.textSecondary }}>
        <b style={{ color: t.c.text }}>Nothing recorded for this window.</b>{" "}
        {who}’s account is empty here — no tracked activity and no written analysis.
        Check the date range or the device status.
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LEVEL 2 — one stretch, opened
   ═══════════════════════════════════════════════════════════════════════════ */


/* ═══════════════════════════════════════════════════════════════════════════
   LIVE STRETCH (2026-08-30) — a stretch rebuilt from the trackers that nobody
   has written up yet. The old page said "nothing distinctive enough to name
   the work" and stopped. Now it (a) lays out every tracked fact it has —
   tools with icons, window titles, share — and (b) offers to write the hour
   around it up on demand (work-summary-new /hour-detail, cached, one credit).
   ═══════════════════════════════════════════════════════════════════════════ */
function useHourWriteup() {
  const [st, setSt] = React.useState({ running: false, headline: null, points: [], insight: null, facts: null, error: null, done: false, cached: false });
  const run = React.useCallback(async ({ userId, date, hour, source = "both", force = false }) => {
    setSt({ running: true, headline: null, points: [], insight: null, facts: null, error: null, done: false, cached: false });
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/hour-detail`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, date, source, hour, force: !!force }),
      });
      if (!res.ok || !res.body) { let m = "Could not write this up."; try { const j = await res.json(); m = j?.message || m; } catch (_) { /* stream */ } setSt((x) => ({ ...x, running: false, error: m, done: true })); return; }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true }); const lines = buf.split("\n"); buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue; let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
          if (d.type === "facts") setSt((x) => ({ ...x, facts: d.facts, cached: !!d.cached }));
          else if (d.type === "headline") setSt((x) => ({ ...x, headline: d.text }));
          else if (d.type === "point") setSt((x) => ({ ...x, points: [...x.points, { text: d.text, tool: d.tool || null }] }));
          else if (d.type === "insight") setSt((x) => ({ ...x, insight: d.text }));
          else if (d.type === "error") setSt((x) => ({ ...x, error: d.message }));
          else if (d.type === "done") setSt((x) => ({ ...x, running: false, done: true }));
        }
      }
      setSt((x) => ({ ...x, running: false, done: true }));
    } catch (e) { setSt((x) => ({ ...x, running: false, error: e.message || "Could not write this up.", done: true })); }
  }, []);
  return { ...st, run };
}

function domainIn(text) {
  const m = String(text || "").match(/(?:https?:\/\/)?(?:www\.)?([a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9-]+)+)/i);
  if (!m) return null;
  const d = m[1].toLowerCase().replace(/\/.*$/, "");
  if (!/\.[a-z]{2,}$/i.test(d)) return null;
  return d;
}

const WEAK_HOST = new Set(["www", "docs", "mail", "app", "api", "com", "org", "net", "io", "dev", "web", "cdn"]);

function toolFromActivity(a, low) {
  const lab = String(a && a.label || "").trim();
  if (!lab) return null;
  const dom = domainIn(lab) || domainIn(a.domain || a.url || "");
  const site = Boolean(dom || /website|url|site/i.test(String(a.type || "")));
  const host = dom ? dom.split(".")[0] : "";
  const hit = (lab.length >= 3 && low.includes(lab.toLowerCase()))
    || (dom && low.includes(dom))
    || (host.length >= 5 && !WEAK_HOST.has(host) && low.includes(host));
  if (!hit) return null;
  return { name: dom || lab, type: site ? "website" : a.type };
}

/** Tracked label → any domain in the line → known glyph. New sites need no list. */
function toolHintFromPoint(pt, activities) {
  const text = String(pt || "");
  const low = text.toLowerCase();
  for (const a of activities || []) {
    const hit = toolFromActivity(a, low);
    if (hit) return hit;
  }
  const dom = domainIn(text);
  if (dom) return { name: dom, type: "website" };
  if (/seller\s*7|seller7|pricing hub|catalog manager/i.test(text)) return { name: "Seller7" };
  if (/swagger|openapi/i.test(text)) return { name: "Swagger" };
  if (/google\s+docs?|untitled document/i.test(text)) return { name: "Google Docs" };
  if (/\bamazon s3\b|\bs3\b|gzip/i.test(text)) return { name: "Amazon S3" };
  if (/postman|api client/i.test(text)) return { name: "Postman" };
  const known = toolNameInText(text);
  if (known) return { name: known };
  return null;
}

function fallbackTool(s, acts) {
  const labeled = (acts || []).find((a) => a && a.label && (toolGlyphIdFor(a.label) || domainIn(a.label)));
  if (labeled) {
    const lab = labeled.label;
    const dom = domainIn(lab);
    return { name: dom || lab, type: dom ? "website" : labeled.type };
  }
  if (s && s.app) return { name: s.app };
  if (acts && acts[0] && acts[0].label) return { name: acts[0].label };
  return { name: "Work" };
}

function skuTokens(text) {
  return [...new Set(String(text || "").match(/\b[A-Z]{2,}[A-Z0-9][A-Z0-9._-]{3,}\b/g) || [])].slice(0, 6);
}

function prettyToolName(name) {
  const n = String(name || "").trim();
  if (/seller\s*7|seller7/i.test(n)) return "Seller7";
  if (/google\s+docs?/i.test(n)) return "Google Docs";
  if (/amazon s3|^s3$/i.test(n)) return "Amazon S3";
  if (n.includes(".")) return n.replace(/^www\./i, "");
  return n.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function pointText(pt) {
  return typeof pt === "string" ? pt : String(pt && pt.text || "");
}

function stampedTool(pt) {
  const raw = pt && typeof pt === "object" ? pt.tool : null;
  if (!raw || !raw.name) return null;
  return { name: raw.host || raw.name, type: (raw.host || raw.url) ? "website" : raw.type };
}

function toolFromBlocks(text, blocks) {
  const low = String(text || "").toLowerCase();
  if (!low || !blocks || !blocks.length) return null;
  let best = null;
  let score = 0;
  for (const b of blocks) {
    let n = 0;
    const app = String(b.app || "").toLowerCase();
    if (app.length >= 3 && low.includes(app)) n += 4;
    const host = domainIn(b.url || "");
    if (host && low.includes(host)) n += 4;
    const stem = host ? host.split(".")[0] : "";
    if (stem.length >= 5 && low.includes(stem)) n += 2;
    if (n > score) { score = n; best = b; }
  }
  if (score < 2 || !best) return null;
  const host = domainIn(best.url || "");
  return { name: host || best.app, type: host ? "website" : undefined };
}

function toolFromHourApps(text, apps) {
  const list = apps || [];
  if (!list.length) return null;
  const low = String(text || "").toLowerCase();
  const named = list.find((a) => a && a.name && low.includes(String(a.name).toLowerCase()));
  if (named) return { name: named.name };
  if (/\b(command|whisper|ffmpeg|transcri|terminal|shell)\b|\.mp4\b/i.test(low)) {
    const term = list.find((a) => /terminal|iterm|cmd|powershell|warp|cursor|code|python/i.test(String(a.name || "")));
    if (term) return { name: term.name };
  }
  return list[0] && list[0].name ? { name: list[0].name } : null;
}

function groupPointsByTool(points, activities, facts) {
  const blocks = facts && facts.blocksList;
  const apps = facts && facts.apps;
  const order = [];
  const by = new Map();
  (points || []).forEach((pt, i) => {
    const text = pointText(pt);
    const tool = stampedTool(pt) || toolHintFromPoint(text, activities) || toolFromBlocks(text, blocks) || toolFromHourApps(text, apps);
    const label = tool ? prettyToolName(tool.name) : null;
    const key = label || `solo:${i}`;
    if (!by.has(key)) {
      by.set(key, { tool, label, lines: [] });
      order.push(key);
    }
    const line = label ? pointWithoutTool(text, label, tool.name) : text;
    if (line && !by.get(key).lines.includes(line)) by.get(key).lines.push(line);
  });
  return order.map((k) => by.get(k));
}

function splitFactLine(text) {
  const bits = String(text || "")
    .split(/(?<=[.!?])\s+(?=[A-Z“"'])/)
    .map((s) => s.trim())
    .filter((s) => s.length > 8);
  return bits.length ? bits : (String(text || "").trim() ? [String(text).trim()] : []);
}

/** Tool once, then the facts. Chrome is not stamped on every line. */
function factGroups(points, activities, facts) {
  return groupPointsByTool(points, activities, facts)
    .map((g) => {
      const lines = [];
      for (const line of g.lines) {
        for (const text of splitFactLine(line)) {
          if (!lines.includes(text)) lines.push(text);
        }
      }
      return { ...g, lines };
    })
    .filter((g) => g.lines.length);
}

function pointWithoutTool(pt, label, raw) {
  let s = String(pt || "").trim();
  const names = [...new Set([label, raw, /seller7/i.test(`${label} ${raw}`) ? "Seller7" : null, /seller7/i.test(`${label} ${raw}`) ? "Seller 7" : null].filter(Boolean))];
  for (const n of names) {
    const esc = String(n).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    s = s.replace(new RegExp(`^(?:the\\s+)?${esc}(?:\\.ai)?[\\s:–—,.-]*`, "i"), "");
  }
  if (s.length < 10) return String(pt || "").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** First complete sentence + the rest. Never mid-word, never an ellipsis. */
function splitInsight(text) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (!s) return { lead: "", rest: "" };
  const parts = s.split(SENT_SPLIT).map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2 && parts[0].length >= 12) {
    return { lead: parts[0], rest: parts.slice(1).join(" ") };
  }
  return { lead: s, rest: "" };
}

export function LiveStretchFacts({ t, s, userId, embedded = false }) {
  const w = useHourWriteup();
  const hour = s.t0 ? Number(String(s.t0).slice(0, 2)) : null;
  const acts = (s.activities || []).filter((a) => a && a.label);
  const totalW = acts.reduce((a, x) => a + (Number(x.weight) || 0), 0) || 1;
  const uid = userId || s.userId || s.user?.id || null;
  const day = s.dateLocal || s.date || s.day || null;
  const canWrite = Boolean(uid && day && Number.isFinite(hour));
  /* Auto-write (user, 2026-08-30: "ye generate ho jaana chahiye"). Opening a
     live stretch writes its hour up straight away — the server caches the
     result for the day, so re-opening replays it for free. Keyed on the
     stretch so a Prev/Next jump writes the new one. */
  const runRef = React.useRef(w.run);
  runRef.current = w.run;
  React.useEffect(() => {
    if (!canWrite) return undefined;
    runRef.current({ userId: uid, date: day, hour });
    return undefined;
  }, [canWrite, uid, day, hour, s.id]);
  const band = w.error
    ? { c: t.status.negative, text: <><b>Could not write this up.</b> The tracked facts below still stand.</> }
    : w.running
      ? { c: t.c.ai, text: <><b>Writing this stretch up…</b> facts first, the AI read lands in a few seconds.</> }
      : w.done
        ? { c: t.status.positive, text: <><b>Written up{w.cached ? " — from today's cache" : " just now"}.</b> Every fact below is from the tracker; nothing is invented.</> }
        : canWrite
          ? { c: t.status.warning, text: <><b>Tracked, not yet written up.</b> Every fact below is from the tracker; nothing is invented.</> }
          : { c: t.status.warning, text: <><b>Tracked, from the tracker.</b> Nothing is invented{Number.isFinite(hour) ? "" : " — no clock time on this stretch, so there is no hour to write up"}.</> };
  return (
    <div>
      {(!w.done || w.error) ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: t.r.md,
          background: `${band.c}1f`, border: `1px solid ${band.c}55`, fontSize: 12.5, color: t.c.text, flexWrap: "wrap",
          marginBottom: acts.length || w.running ? 12 : 0,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: band.c, flex: "none" }} />
          <span>{band.text}</span>
        </div>
      ) : null}

      {acts.length > 0 && !(w.done && w.points.length) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {acts.map((a, i) => {
            const pct = Math.round(((Number(a.weight) || 0) / totalW) * 100);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "30px 1fr 64px", gap: 10, alignItems: "center", padding: "9px 12px", borderRadius: t.r.md, background: t.c.surfaceMuted }}>
                <ToolIcon name={a.label} type={a.type} size={30} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.c.text }}>{a.label}</span>
                  {a.detail && <span style={{ display: "block", fontSize: 12, color: t.c.textSecondary, lineHeight: 1.5, marginTop: 2 }}>{a.detail}</span>}
                  <span style={{ display: "block", height: 5, borderRadius: 99, background: t.c.surfaceHover, marginTop: 7, overflow: "hidden" }}><i style={{ display: "block", height: "100%", width: `${pct}%`, background: t.c.primary, borderRadius: 99 }} /></span>
                </span>
                <b style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums", textAlign: "right", color: t.c.text }}>{pct}%</b>
              </div>
            );
          })}
        </div>
      )}

      {(w.running || w.done) && (() => {
        const h12 = (h) => `${h % 12 || 12}${h < 12 ? " AM" : " PM"}`;
        const span = Number.isFinite(hour) ? `${h12(hour)} – ${h12((hour + 1) % 24)}` : "";
        const when = [span, s.dayLabel || s.dateLocal].filter(Boolean).join(" · ");
        const page = t.isDark ? t.c.surface : "#fff";
        const rowBg = embedded ? paper(t).well : t.c.surfaceMuted;
        return (
          <article style={embedded ? { marginTop: 2 } : {
            marginTop: 16, borderRadius: 14, background: page,
            border: `1px solid ${t.c.border}`, padding: "16px 18px 14px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Sparkles size={13} strokeWidth={2.2} color={t.c.ai} />
              <span style={{ fontSize: 12.5, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                {when || "This hour"}
              </span>
              <span style={{ flex: 1 }} />
              {canWrite && w.done ? (
                <button type="button" onClick={() => w.run({ userId: uid, date: day, hour, force: true })}
                  style={{
                    fontSize: 12, fontWeight: 650, padding: 0, border: 0, background: "none",
                    color: t.c.ai, cursor: "pointer", fontFamily: t.font.family,
                  }}>
                  {w.error ? "Try again" : "Rewrite"}
                </button>
              ) : null}
            </div>

            {w.error ? (
              <div style={{ padding: "10px 12px", borderRadius: 10, background: t.status.negativeSoft, color: t.status.negative, fontSize: 13 }}>{w.error}</div>
            ) : (
              <div>
                  {w.headline ? (
                    <p style={{
                      margin: 0, fontSize: 16.5, fontWeight: 650, lineHeight: 1.35,
                      letterSpacing: "-0.3px", color: t.c.text,
                    }}>{w.headline}</p>
                  ) : (
                    <><Shimmer w="92%" h={16} /><div style={{ height: 8 }} /><Shimmer w="54%" h={16} /></>
                  )}
                  {w.insight ? (() => {
                    const { lead, rest } = splitInsight(w.insight);
                    const skus = skuTokens(w.insight);
                    return (
                      <aside style={{
                        marginTop: 14, background: t.isDark ? t.c.surfaceMuted : "#1D1F21",
                        color: "#fff", borderRadius: 14, overflow: "hidden",
                      }}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "11px 16px", background: t.c.ai,
                        }}>
                          <span aria-hidden="true" style={{
                            width: 24, height: 24, borderRadius: 7, flexShrink: 0,
                            display: "grid", placeItems: "center",
                            background: "rgba(255,255,255,.2)", color: "#fff",
                          }}><Sparkles size={12} strokeWidth={2.3} /></span>
                          <span style={{ fontSize: 13.5, fontWeight: 650, letterSpacing: "-0.15px" }}>Worth a look</span>
                        </div>
                        <div style={{ padding: "14px 16px 16px" }}>
                          {lead ? (
                            <p style={{
                              margin: 0, fontSize: rest ? 15 : 14.5, fontWeight: rest ? 650 : 500,
                              letterSpacing: "-0.15px", lineHeight: 1.5,
                              overflowWrap: "break-word", wordBreak: "normal",
                            }}>{lead}</p>
                          ) : null}
                          {rest ? (
                            <p style={{
                              margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.55, opacity: 0.9,
                              overflowWrap: "break-word", wordBreak: "normal",
                            }}>{rest}</p>
                          ) : null}
                          {skus.length ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 12 }}>
                              {skus.map((k) => (
                                <span key={k} style={{
                                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  fontSize: 10.5, padding: "3px 7px", borderRadius: 999,
                                  background: "rgba(255,255,255,.1)",
                                }}>{k}</span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </aside>
                    );
                  })() : null}
                  {(w.points.length > 0 || w.running) ? (
                    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                      {factGroups(w.points, acts, w.facts).map((g, gi) => {
                        const icon = g.tool || fallbackTool(s, acts);
                        const card = t.isDark ? paper(t).well : (embedded ? "#fff" : rowBg);
                        return (
                          <div key={g.label || gi} style={{
                            borderRadius: 14, overflow: "hidden", background: card,
                            boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
                          }}>
                            {(g.label || icon) ? (
                              <div style={{
                                display: "flex", alignItems: "center", gap: 10,
                                padding: "12px 14px 4px",
                              }}>
                                {icon ? <ToolIcon name={icon.name} type={icon.type} size={28} /> : null}
                                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 650, letterSpacing: "-0.2px" }}>
                                  {g.label || icon?.name}
                                </span>
                                <span style={{ fontSize: 12, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                                  {g.lines.length}
                                </span>
                              </div>
                            ) : null}
                            <ol style={{ margin: 0, padding: "6px 8px 10px", listStyle: "none" }}>
                              {g.lines.map((text, i) => (
                                <li key={i} style={{
                                  display: "grid", gridTemplateColumns: "28px 1fr", gap: 10, alignItems: "start",
                                  padding: "8px 8px",
                                  borderTop: i === 0 ? "none" : `1px solid ${t.isDark ? "rgba(255,255,255,.06)" : "rgba(24,32,46,.06)"}`,
                                }}>
                                  <span aria-hidden="true" style={{
                                    width: 22, height: 22, borderRadius: 7, marginTop: 1,
                                    display: "grid", placeItems: "center",
                                    background: t.c.primary, color: "#fff",
                                    fontSize: 10.5, fontWeight: 750, fontVariantNumeric: "tabular-nums",
                                  }}>{String(i + 1).padStart(2, "0")}</span>
                                  <span style={{ fontSize: 14, fontWeight: 550, lineHeight: 1.4, letterSpacing: "-0.15px", color: t.c.text, overflowWrap: "break-word", minWidth: 0 }}>
                                    {text}
                                  </span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        );
                      })}
                      {w.running ? (
                        <div style={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: 12, padding: "11px 12px", borderRadius: 12, background: rowBg }}>
                          <span style={{ width: 32, height: 32, borderRadius: 8, background: t.c.border }} />
                          <Shimmer w="78%" h={13} />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
              </div>
            )}
          </article>
        );
      })()}
    </div>
  );
}

export function StretchAnalysis({ stretch, index, total, timeline, onBack, onJump, onFullLog, numbered = false, userId = null }) {
  const t = useDashTheme();
  const s = stretch;
  if (!s) return null;

  const h0 = s.t0 ? Number(s.t0.slice(0, 2)) : null;
  const h1 = s.t1 ? Number(s.t1.slice(0, 2)) : h0;
  const dayHours = (timeline || [])
    .filter((h) => String(h.hourKey || "").slice(0, 10) === s.dateLocal)
    .map((h) => ({ ...h, hour: Number(String(h.hourKey).slice(11, 13)) }));

  const weighted = s.activities.filter((a) => a.weight > 0);
  const totalWeight = weighted.reduce((a, x) => a + x.weight, 0) || 1;

  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.22, ease: t.motion.ease.out }}
      key={s.id}
    >
      {/* nav */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <TextBtn t={t} onClick={onBack}><ChevronLeft size={14} /> All takeaways</TextBtn>
        <span style={{ flex: 1 }} />
        <TextBtn t={t} disabled={index <= 0} onClick={() => onJump(index - 1)}>
          <ChevronLeft size={14} /> Previous
        </TextBtn>
        <TextBtn t={t} disabled={index >= total - 1} onClick={() => onJump(index + 1)}>
          Next <ChevronRight size={14} />
        </TextBtn>
      </div>

      {/* head — solid: kicker in ink, the when as a chip row, then the title */}
      <div style={{ paddingBottom: 18, borderBottom: `1px solid ${t.c.border}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: t.c.ai, marginBottom: 8 }}>
          One stretch · {s.source === "live" ? "from tracking" : s.source === "hourly-insights" ? "hourly analysis" : "written analysis"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
          {[
            { l: s.dayLabel || s.dateLocal, dot: t.c.ai },
            s.t0 ? { l: `${s.t0} → ${s.t1 || ""}` } : null,
            { l: s.durationLabel, tone: t.c.primary },
          ].filter((c) => c && c.l).map((c, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 650, padding: "6px 12px", borderRadius: 999, background: c.tone ? `${c.tone}1f` : t.c.surfaceMuted, border: `1px solid ${c.tone ? `${c.tone}55` : t.c.border}`, color: c.tone || t.c.text }}>
              {c.dot ? <i style={{ width: 6, height: 6, borderRadius: 99, background: c.dot }} /> : null}{c.l}
            </span>
          ))}
        </div>
        <h2 style={{
          fontSize: 24, fontWeight: 700, letterSpacing: "-0.6px", lineHeight: 1.25,
          margin: "12px 0 0", color: t.c.text,
        }}>{String(s.title || "").replace(/\s*[•·]\s*$/, "") || "Tracked work"}</h2>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 11 }}>
          {s.project
            ? <Pill t={t} tone="project">{s.project}</Pill>
            : <Pill t={t} tone="warn">no project attributed</Pill>}
          <Pill t={t}>{s.intensity} intensity</Pill>
          {s.unclear
            ? <Pill t={t} tone="warn">not yet analysed</Pill>
            : <Pill t={t} tone="ok">analysed</Pill>}
          {s.source === "live" && <Pill t={t}>from tracking</Pill>}
          {s.granularity === "hour" && <Pill t={t}>hour-level</Pill>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.35fr) minmax(0,1fr)", gap: 32 }}>
        <div>
          <div style={{ marginTop: 22 }}>
            <SecLabel t={t} hint={s.summary ? "as written" : undefined}>What happened</SecLabel>
          </div>
          {s.summary ? (
            <Prose text={s.summary} numbered={numbered} />
          ) : s.source === "live" ? (
            <LiveStretchFacts t={t} s={s} userId={userId} />
          ) : (
            <p style={{ fontSize: 13.5, lineHeight: 1.7, color: t.c.textTertiary, margin: 0 }}>
              Nothing in this stretch was distinctive enough to name the work, so no title was invented for it.
              The time is real and counted; the attribution is honestly absent.
            </p>
          )}

          {s.activities.length > 0 && s.source !== "live" && (
            <>
              <div style={{ marginTop: 24 }}>
                <SecLabel t={t} hint={weighted.length ? "share of this stretch" : "in order"}>The breakdown</SecLabel>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {s.activities.map((a, i) => {
                  const pct = a.weight > 0 ? Math.round((a.weight / totalWeight) * 100) : null;
                  return (
                    <div key={i} style={{ padding: "10px 12px", borderRadius: t.r.md, background: t.c.surfaceMuted, display: "grid", gridTemplateColumns: "30px 1fr", gap: 10, alignItems: "start" }}>
                      {/* A real tool gets its icon; a task label gets a numbered ink tile — never initials. */}
                      {(toolGlyphIdFor(a.label) || brandColorFor(a.label, t))
                        ? <ToolIcon name={a.label} size={30} />
                        : <span style={{ width: 30, height: 30, borderRadius: 8, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, fontVariantNumeric: "tabular-nums", background: `${t.c.primary}1c`, color: t.c.primary }}>{String(i + 1).padStart(2, "0")}</span>}
                      <div>
                      <div style={{
                        display: "flex", alignItems: "baseline", justifyContent: "space-between",
                        gap: 8, fontSize: 12.5, fontWeight: 600, color: t.c.text,
                      }}>
                        <span>{a.label}</span>
                        {pct != null && (
                          <span style={{ fontSize: 11, fontWeight: 500, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                            {pct}%
                          </span>
                        )}
                      </div>
                      {a.detail && (
                        <div style={{ fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.5, marginTop: 4 }}>{a.detail}</div>
                      )}
                      {pct != null && (
                        <div style={{ height: 5, borderRadius: 99, background: t.c.surfaceHover, marginTop: 8, overflow: "hidden" }}>
                          <span style={{ display: "block", height: "100%", borderRadius: 99, width: `${pct}%`, background: t.c.primary }} />
                        </div>
                      )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div>
          <div style={{ marginTop: 22 }}><SecLabel t={t} hint="org-local">When</SecLabel></div>
          {dayHours.length > 0 && h0 != null ? (
            <HourStrip hours={dayHours} highlight={[h0, h1]} compact />
          ) : h0 != null ? (
            /* No per-hour record for the day — still place the stretch on a plain 24h clock. */
            <div>
              <div style={{ position: "relative", height: 14, borderRadius: 99, background: t.c.surfaceHover, overflow: "hidden" }}>
                {(() => { const a = Number(s.t0.slice(0, 2)) * 60 + Number(s.t0.slice(3, 5)); const b = s.t1 ? Number(s.t1.slice(0, 2)) * 60 + Number(s.t1.slice(3, 5)) : a + 30; return <span style={{ position: "absolute", top: 2, bottom: 2, left: `${(a / 1440) * 100}%`, width: `${Math.max(0.8, ((b - a) / 1440) * 100)}%`, borderRadius: 99, background: t.c.primary }} />; })()}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: t.c.textTertiary, marginTop: 4 }}><span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span></div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: t.c.textTertiary, lineHeight: 1.6 }}>
              This stretch carries no clock times, so it cannot be placed on the day.
            </div>
          )}

          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 16 }}>
            {[["Duration", s.durationLabel], ["Started", s.t0 || "—"], ["Ended", s.t1 || "—"]].map(([k, v]) => (
              <div key={k} style={{ fontSize: 12, color: t.c.textTertiary }}>
                {k}
                <b style={{
                  display: "block", fontSize: 14, color: t.c.text, fontWeight: 600,
                  fontVariantNumeric: "tabular-nums", marginTop: 2,
                }}>{v}</b>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 24 }}><SecLabel t={t}>Behind this</SecLabel></div>
          <div style={{
            padding: "12px 14px", borderRadius: t.r.md, background: t.c.primarySoft,
            boxShadow: `0 0 0 1px ${t.c.primaryRing}`, fontSize: 12.5, lineHeight: 1.6, color: t.c.text,
          }}>
            {s.source === "live"
              ? "Rebuilt from the tracker's own record for this day — times, apps and project exactly as tracked. Nothing has been written about it yet, so no narrative is shown and none is invented. No model call was made."
              : s.source === "hourly-insights"
                ? "Read from the stored hourly analysis for this period. No fresh model call was made to open this."
                : "Read from the stored analysis for this day — the same record the Work Summary page uses. No fresh model call was made to open this."}
          </div>

          <div style={{ marginTop: 16 }}>
            <LogDoor t={t} onClick={onFullLog} sub={total ? `every stretch of the day, in order · ${total} stretch${total === 1 ? "" : "es"}` : "every stretch of the day, in order"} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/** The door to the whole log — one anatomy everywhere it appears (2026-08-30):
 *  ink tile · title · what's behind it · arrow, on a raised card. Replaces
 *  the bare blue text link that read like an afterthought. */
export function LogDoor({ t, onClick, title = "Open the full work log", sub = "every stretch of the day, in order", icon = null }) {
  const [hover, setHover] = React.useState(false);
  const ink = t.c.primary;
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 14, width: "100%", textAlign: "left",
        padding: "14px 16px", borderRadius: 16, cursor: "pointer", fontFamily: t.font.family, color: t.c.text,
        background: hover ? `${ink}14` : (t.isDark ? t.c.surface : "#E8EDF5"),
        border: `1px solid ${hover ? `${ink}66` : t.c.border}`,
        boxShadow: hover ? `0 10px 24px -16px ${ink}99` : t.shadow.raised,
        transition: "background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .15s ease",
        transform: hover ? "translateY(-1px)" : "none",
      }}>
      <span aria-hidden="true" style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: "grid", placeItems: "center", color: "#fff", background: `linear-gradient(135deg, color-mix(in srgb, ${ink} 78%, #fff), ${ink} 58%, color-mix(in srgb, ${ink} 82%, #000))`, boxShadow: `0 8px 16px -8px ${ink}` }}>
        {icon || <FileText size={18} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14, fontWeight: 700, letterSpacing: "-0.2px" }}>{title}</span>
        <span style={{ display: "block", fontSize: 12, color: t.c.textSecondary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
      </span>
      <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 999, flexShrink: 0, display: "grid", placeItems: "center", background: hover ? ink : `${ink}1a`, color: hover ? "#fff" : ink, transition: "background .15s, color .15s" }}>
        <ChevronRight size={15} style={{ transform: hover ? "translateX(1px)" : "none", transition: "transform .15s" }} />
      </span>
    </button>
  );
}

function TextBtn({ t, children, onClick, disabled, style }) {
  /* Pill buttons (2026-08-30): the old bare blue text read as a label. */
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px",
        border: `1px solid ${disabled ? t.c.border : `${t.c.primary}44`}`, borderRadius: 999,
        background: disabled ? "transparent" : `${t.c.primary}14`,
        color: disabled ? t.c.textTertiary : t.c.primary,
        fontSize: 12.5, fontWeight: 650, fontFamily: t.font.family, lineHeight: 1,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1,
        ...style,
      }}
    >{children}</button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LEVEL 2 — the whole window
   ═══════════════════════════════════════════════════════════════════════════ */

export function FullWorkLog({ worklog, onPick }) {
  const t = useDashTheme();
  const days = (worklog?.days || []).filter((d) => d.stretches.length > 0);

  let running = -1;

  return (
    <>
      <h3 style={{ fontSize: t.font.size.lg, fontWeight: 600, margin: "0 0 3px", color: t.c.text }}>Work log</h3>
      <p style={{ fontSize: 12, color: t.c.textTertiary, margin: "0 0 16px", lineHeight: 1.55 }}>
        Every stretch in the window, in order, with what was behind it. Written analysis where it exists,
        and an explicit “couldn’t be named” where it does not — nothing here is inferred from hours alone.
      </p>

      <div style={{
        padding: "12px 14px", borderRadius: t.r.md, marginBottom: 20,
        background: t.c.primarySoft, boxShadow: `0 0 0 1px ${t.c.primaryRing}`,
        fontSize: 12.5, lineHeight: 1.6, color: t.c.text,
      }}>
        <b>{worklog?.coverage?.label}.</b>{" "}
        Anything without an analysis is still real tracked time — it appears in the totals and on the
        timeline, but it is never given a made-up title.
      </div>

      {worklog?.rollup?.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SecLabel t={t} hint="rolled up across the window">Where the effort went</SecLabel>
          <RollupBars rows={worklog.rollup} />
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {days.map((day) => (
          <div key={day.dateLocal} style={{
            borderRadius: t.r.md, background: t.c.surface,
            boxShadow: `0 0 0 1px ${t.c.border}`, overflow: "hidden",
          }}>
            <div style={{
              display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
              padding: "13px 16px", borderBottom: `1px solid ${t.c.border}`, background: t.c.surfaceMuted,
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: t.c.text }}>{day.label}</span>
              {day.headline && (
                <span style={{ fontSize: 12.5, color: t.c.textSecondary, flex: "1 1 260px", minWidth: 0, lineHeight: 1.5 }}>
                  {day.headline}
                </span>
              )}
              <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: t.c.text }}>
                {day.totalFormatted}
              </span>
            </div>

            <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
              {day.stretches.map((s) => {
                running += 1;
                const idx = running;
                return (
                  <div key={s.id || idx} style={{ display: "grid", gridTemplateColumns: "92px minmax(0,1fr)", gap: 14 }}>
                    <div style={{ fontSize: 11.5, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums", paddingTop: 2 }}>
                      {s.t0 ? `${s.t0} – ${s.t1 || ""}` : "—"}
                      <span style={{ display: "block", fontSize: 10.5, color: t.c.textTertiary, marginTop: 3 }}>
                        {s.durationLabel}
                      </span>
                    </div>
                    <div>
                      <button
                        onClick={() => onPick(idx)}
                        style={{
                          border: "none", background: "none", padding: 0, textAlign: "left",
                          cursor: "pointer", fontFamily: t.font.family, color: t.c.text,
                          fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.1px",
                          display: "inline-flex", alignItems: "center", gap: 5,
                        }}
                      >
                        {s.title}<ChevronRight size={14} style={{ color: t.c.textTertiary }} />
                      </button>
                      <div style={{ display: "flex", gap: 7, marginTop: 6, flexWrap: "wrap" }}>
                        {s.project
                          ? <Pill t={t} tone="project">{s.project}</Pill>
                          : <Pill t={t} tone="warn">no project</Pill>}
                        <Pill t={t}>{s.intensity} intensity</Pill>
                      </div>
                      {s.summary ? (
                        <div style={{ marginTop: 8 }}><Prose text={s.summary} /></div>
                      ) : (
                        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t.c.textTertiary, marginTop: 7, fontStyle: "italic" }}>
                          No written analysis for this stretch.
                        </div>
                      )}
                      {s.activities.length > 0 && (
                        <div style={{
                          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                          gap: 10, marginTop: 11,
                        }}>
                          {s.activities.map((a, i) => (
                            <div key={i} style={{ padding: "9px 11px", borderRadius: t.r.md, background: t.c.surfaceMuted }}>
                              <div style={{
                                display: "flex", alignItems: "baseline", justifyContent: "space-between",
                                gap: 8, fontSize: 12, fontWeight: 600, color: t.c.text,
                              }}>
                                <span>{a.label}</span>
                                {a.weight > 0 && (
                                  <span style={{ fontSize: 11, fontWeight: 500, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                                    {a.weight}%
                                  </span>
                                )}
                              </div>
                              {a.detail && (
                                <div style={{ fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.5, marginTop: 4 }}>{a.detail}</div>
                              )}
                              {a.weight > 0 && (
                                <div style={{ height: 4, borderRadius: 99, background: t.c.surfaceHover, marginTop: 8, overflow: "hidden" }}>
                                  <span style={{ display: "block", height: "100%", borderRadius: 99, width: `${a.weight}%`, background: t.c.primary }} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!days.length && (
        <div style={{
          padding: "16px", borderRadius: t.r.md, background: t.c.surfaceMuted,
          fontSize: 13, color: t.c.textTertiary, lineHeight: 1.6, display: "flex", gap: 10,
        }}>
          <FileText size={17} style={{ flexShrink: 0, marginTop: 1 }} />
          No written analysis exists for any day in this window. The tracked hours are real; the account of
          what filled them has not been generated.
        </div>
      )}
    </>
  );
}

export function WorkLogSkeleton() {
  const t = useDashTheme();
  return (
    <div>
      <div style={{ padding: 14, borderRadius: t.r.md, background: t.c.surfaceMuted, marginBottom: 12 }}>
        <Shimmer w="35%" h={9} /><div style={{ height: 9 }} />
        <Shimmer w="100%" h={12} /><div style={{ height: 6 }} />
        <Shimmer w="70%" h={12} />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "44px 12px 1fr", gap: 8, marginBottom: 8 }}>
          <div style={{ paddingTop: 12 }}><Shimmer w={32} h={9} /></div>
          <div />
          <div style={{ padding: "11px 13px", borderRadius: t.r.md, background: t.c.surfaceMuted }}>
            <Shimmer w="65%" h={11} /><div style={{ height: 8 }} /><Shimmer w="40%" h={9} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Flatten the day-nested log into the stable index the two tiers share. */
export function flattenStretches(worklog) {
  const out = [];
  for (const day of worklog?.days || []) {
    for (const s of day.stretches) out.push({ ...s, dayLabel: day.label });
  }
  return out;
}

export default TakeawayList;
